import { useState, useCallback } from "react";
import {
  profitTrackingService,
  ProfitEntry,
  CountrySummary,
  PaymentMethodSummary,
  DailyProfit,
  GlobalSummary,
} from "../../../../services/profitTrackingService";
import { FilterParams } from "../types/profitTracking";

export const useProfitData = () => {
  const [globalSummary, setGlobalSummary] = useState<GlobalSummary | null>(null);
  const [countrySummary, setCountrySummary] = useState<CountrySummary[]>([]);
  const [paymentMethodSummary, setPaymentMethodSummary] = useState<PaymentMethodSummary[]>([]);
  const [dailyProfits, setDailyProfits] = useState<DailyProfit[]>([]);
  const [entries, setEntries] = useState<ProfitEntry[]>([]);
  const [totalEntries, setTotalEntries] = useState(0);
  const [allEntries, setAllEntries] = useState<ProfitEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [countries, setCountries] = useState<string[]>([]);

  const loadOverview = useCallback(async (params: FilterParams) => {
    setLoading(true);
    try {
      const [gRes, cRes, pmRes] = await Promise.all([
        profitTrackingService.globalSummary(params),
        profitTrackingService.summaryByCountry(params),
        profitTrackingService.summaryByPaymentMethod(params),
      ]);
      setGlobalSummary(gRes.data);
      setCountrySummary(cRes.data || []);
      setPaymentMethodSummary(pmRes.data || []);
    } catch (err) {
      console.error("Error loading overview", err);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadEntries = useCallback(async (params: FilterParams & { skip: number; limit: number }) => {
    setLoading(true);
    try {
      const res = await profitTrackingService.listEntries(params);
      setEntries(res.data?.entries || []);
      setTotalEntries(res.data?.total || 0);
    } catch (err) {
      console.error("Error loading entries", err);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadChartsData = useCallback(async (params: FilterParams) => {
    setLoading(true);
    try {
      // Le backend limite `limit` à 200. On pagine jusqu'à récupérer toutes les entrées
      // (plafond de sécurité : 50 pages × 200 = 10 000 entrées max).
      const PAGE_SIZE = 200;
      const MAX_PAGES = 50;
      const fetchAllEntries = async () => {
        const collected: any[] = [];
        let total = 0;
        for (let i = 0; i < MAX_PAGES; i++) {
          const res = await profitTrackingService.listEntries({
            ...params,
            skip: i * PAGE_SIZE,
            limit: PAGE_SIZE,
          });
          const batch = res.data?.entries || [];
          total = res.data?.total ?? total;
          collected.push(...batch);
          if (batch.length < PAGE_SIZE || collected.length >= total) break;
        }
        return collected;
      };

      const [collectedEntries, gRes, cRes, pmRes, dailyRes] = await Promise.all([
        fetchAllEntries(),
        profitTrackingService.globalSummary(params),
        profitTrackingService.summaryByCountry(params),
        profitTrackingService.summaryByPaymentMethod(params),
        profitTrackingService.dailyProfitSummary(params),
      ]);
      setAllEntries(collectedEntries);
      setGlobalSummary(gRes.data);
      setCountrySummary(cRes.data || []);
      setPaymentMethodSummary(pmRes.data || []);
      setDailyProfits(dailyRes.data || []);
    } catch (err) {
      console.error("Error loading charts data", err);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadDaily = useCallback(async (params: FilterParams) => {
    setLoading(true);
    try {
      const res = await profitTrackingService.dailyProfitSummary(params);
      setDailyProfits(res.data || []);
    } catch (err) {
      console.error("Error loading daily", err);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadCountries = useCallback(async () => {
    try {
      const res = await profitTrackingService.getCountries();
      setCountries(res.data || []);
    } catch (err) {
      console.error("ERREUR", err);
    }
  }, []);

  const createEntry = async (data: any) => {
    return await profitTrackingService.createEntry(data);
  };

  const updateEntry = async (id: string, data: any) => {
    return await profitTrackingService.updateEntry(id, data);
  };

  const deleteEntry = async (id: string) => {
    return await profitTrackingService.deleteEntry(id);
  };

  const recalculateAll = async () => {
    return await profitTrackingService.recalculateAll();
  };

  const downloadEntriesExcel = async (params: FilterParams) => {
    return await profitTrackingService.downloadEntriesExcel(params);
  };

  const downloadSummaryByCountryExcel = async (params: FilterParams) => {
    return await profitTrackingService.downloadSummaryByCountryExcel(params);
  };

  const downloadSummaryByPaymentMethodExcel = async (params: FilterParams) => {
    return await profitTrackingService.downloadSummaryByPaymentMethodExcel(params);
  };

  const downloadDailySummaryExcel = async (params: FilterParams) => {
    return await profitTrackingService.downloadDailySummaryExcel(params);
  };

  return {
    // State
    globalSummary,
    countrySummary,
    paymentMethodSummary,
    dailyProfits,
    entries,
    totalEntries,
    allEntries,
    loading,
    countries,
    // Actions
    loadOverview,
    loadEntries,
    loadDaily,
    loadChartsData,
    loadCountries,
    createEntry,
    updateEntry,
    deleteEntry,
    recalculateAll,
    downloadEntriesExcel,
    downloadSummaryByCountryExcel,
    downloadSummaryByPaymentMethodExcel,
    downloadDailySummaryExcel,
  };
};
